import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import NeoCarousel from '@/components/ui/neoCarousel';
import { useUserProfile } from '@/context/UserProfileContext';
import { IMAGES_BASE_URL } from '@/lib/config';
import { cn, renderCaptionWithHashtags } from '@/lib/utils';
import { api } from '@/utils/api';
import { lightImpact } from '@/utils/capacitorUtils';
import { timeAgo } from '@/utils/timeAgo';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { IonButton, IonButtons, IonContent, IonFooter, IonHeader, IonIcon, IonModal, IonRouterLink, IonToolbar, useIonToast } from '@ionic/react';
import { chevronBackOutline, images } from 'ionicons/icons';
import { X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import GolfIcon from './GolfIcon';
import './OpenedPost.css';
import { PostMenu } from './PostMenu';
import { ImageWithFallback } from './ui/fallbackImage';
import { GolfAvatar } from './ui/GolfAvatar';
import LikeButton from './ui/LikeButton';
import { TextChat } from './ui/textchat';

type Comment = {
  id: number;
  content: string;
  createdAt: string;
  likes: number;
  isLiked: boolean;
  user: {
    username: string;
    imageUrl: string | null;
  };
};

type OpenedPostProps = {
  isOpen: boolean;
  onDidDismiss: () => void;
  postId: number;
  user: {
    id: number;
    firstName: string;
    lastName: string;
    imageUrl: string;
    username: string;
  };
  media: { type: 'image' | 'video' | 'gif'; url: string }[];
  likes: number;
  isLiked: boolean;
  course: {
    id: number;
    name: string;
  };
  caption: string;
  comments: number;
  profileId: number;
  onDelete: (postId: number) => void;
  createdAt: string;
};

export const OpenedPost: React.FC<OpenedPostProps> = ({
  isOpen,
  onDidDismiss,
  postId,
  user,
  media,
  likes: initialLikes,
  isLiked: initialIsLiked,
  course,
  caption,
  comments: initialComments,
  profileId,
  onDelete,
  createdAt,
}) => {
  const [isLiked, setIsLiked] = useState(initialIsLiked);
  const [likeCount, setLikeCount] = useState(initialLikes);
  const [commentsList, setCommentsList] = useState<Comment[]>([]);
  const [, setCommentsCount] = useState(initialComments);
  const [isLoadingComments, setIsLoadingComments] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [modalReady, setModalReady] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  const { t } = useTranslation('MGP');
  const { profile: currentUserProfile } = useUserProfile();
  const [present] = useIonToast();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const commentsEndRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLIonFooterElement>(null);

  // Function to handle post sharing
  const handleSharePost = () => {
    const postUrl = `${window.location.origin}/p/${postId}`;

    // Copy to clipboard
    navigator.clipboard.writeText(postUrl)
      .then(() => {
        present({
          message: t('link_copied', { defaultValue: 'Link copied to clipboard' }),
          duration: 2000,
          position: 'bottom',
          color: 'success'
        });
      })
      .catch(err => {
        console.error('Could not copy text: ', err);
        present({
          message: t('copy_failed', { defaultValue: 'Failed to copy link' }),
          duration: 2000,
          position: 'bottom',
          color: 'danger'
        });
      });
  };

  // Fetch comments when the modal is opened
  useEffect(() => {
    if (isOpen) {
      fetchComments();
      // Set a small delay to ensure the modal is fully rendered before showing the carousel
      const timer = setTimeout(() => {
        setModalReady(true);
      }, 100);

      return () => clearTimeout(timer);
    } else {
      setModalReady(false);
    }
  }, [isOpen, postId]);

  // Handle window resize to fix carousel display issues
  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        // Force a re-render of the carousel when window size changes
        setModalReady(false);
        setTimeout(() => setModalReady(true), 50);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);

  // Handle keyboard events for native platforms
  useEffect(() => {
    // Setup keyboard listeners for native platforms
    const setupKeyboardListeners = async () => {
      // Listen for keyboard show events
      await Keyboard.addListener('keyboardWillShow', (info) => {
        setKeyboardHeight(info.keyboardHeight);
        setIsKeyboardVisible(true);
      });

      // Listen for keyboard hide events
      await Keyboard.addListener('keyboardWillHide', () => {
        setKeyboardHeight(0);
        setIsKeyboardVisible(false);
      });
    };

    if (isOpen && Capacitor.isPluginAvailable('Keyboard')) {
      //setupKeyboardListeners();
    }

    return () => {
      // Remove keyboard listeners when component unmounts
      const cleanupKeyboardListeners = async () => {
        await Keyboard.removeAllListeners();
      };

      if (Capacitor.isPluginAvailable('Keyboard')) {
        cleanupKeyboardListeners();
      }
    };
  }, [isOpen]);

  const fetchComments = async () => {
    if (!postId) return;
    console.log('Fetching comments for post:', postId);
    setIsLoadingComments(true);
    try {
      const { data } = await api.get<Comment[]>(`/comments/${postId}`);
      console.log('Fetched comments:', data);
      setCommentsList(data.map((comment: any) => ({
        id: comment.comment.id,
        content: comment.comment.text,
        createdAt: comment.comment.createdAt,
        likes: comment.likes?.length || 0,
        isLiked: comment.isLiked,
        user: {
          username: comment.user.username,
          imageUrl: comment.user.imageUrl,
        },
      })));
    } catch (error) {
      console.error('Failed to load comments:', error);
    } finally {
      setIsLoadingComments(false);
    }
  };

  // Like functionality is now handled by the LikeButton component

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    const tempComment = {
      id: Date.now(),
      content: newComment,
      createdAt: new Date().toISOString(),
      likes: 0,
      isLiked: false,
      user: {
        username: user.username,
        imageUrl: user.imageUrl,
      },
    };

    setCommentsList(prevComments => [...prevComments, tempComment]);
    setCommentsCount(prev => prev + 1);
    setNewComment('');

    try {
      await api.post(`/comments`, {
        postId,
        content: newComment,
      });

      // Scroll to bottom after adding comment
      if (commentsEndRef.current) {
        commentsEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Failed to add comment:', error);
      // Remove the temp comment if the API call fails
      setCommentsList(prevComments => prevComments.filter(c => c.id !== tempComment.id));
      setCommentsCount(prev => prev - 1);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    try {
      await api.delete(`/comments/${commentId}`);
      setCommentsList(prevComments => prevComments.filter(comment => comment.id !== commentId));
      setCommentsCount(prev => prev - 1);
    } catch (error) {
      console.error('Failed to delete comment:', error);
    }
  };

  const handleLikeComment = async (commentId: number) => {
    try {
      // Find the comment to determine if we're liking or unliking
      const comment = commentsList.find(c => c.id === commentId);
      if (!comment) return;

      // Update UI immediately for better user experience
      setCommentsList(prevComments =>
        prevComments.map(c =>
          c.id === commentId
            ? {
              ...c,
              likes: c.isLiked ? c.likes - 1 : c.likes + 1,
              isLiked: !c.isLiked,
            }
            : c
        )
      );

      // Make the appropriate API call based on current like status
      if (!comment.isLiked) {
        // Like the comment
        await api.post(`/comments/likes`, {
          commentId,
          userId: profileId,
        });
        lightImpact(); // Haptic feedback
      } else {
        // Unlike the comment
        await api.post(`/comments/unlike`, {
          commentId,
          userId: profileId,
        });
      }
    } catch (error) {
      console.error('Failed to like/unlike comment:', error);
      // Revert UI changes if API call fails
      fetchComments();
    }
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onDidDismiss}
      className="openedPost"
    >
      <IonHeader className="md:hidden ion-no-border">
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={onDidDismiss}>
              <IonIcon icon={chevronBackOutline} className="size-5 text-foreground" />
            </IonButton>
            <div className="flex items-center">
              <IonRouterLink routerLink={`/u/${user.username}`} onClick={onDidDismiss}>
                <GolfAvatar imageUrl={user.imageUrl} username={user.username} name={`${user.firstName} ${user.lastName}`} size={32} />
              </IonRouterLink>
              <div className="ml-3 text-center">
                <IonRouterLink routerLink={`/u/${user.username}`} onClick={onDidDismiss} className="no-underline">
                  <p className="font-semibold">{user.username}</p>
                </IonRouterLink>
              </div>
            </div>
          </IonButtons>
          <IonButtons slot="end">
            {user.id === profileId && (
              <PostMenu
                postId={String(postId)}
                postUserId={user.id}
                onNavigate={() => onDidDismiss()}
                username={user.username}
                onDelete={() => onDelete(postId)}
              />
            )}
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="flex-1 overflow-hidden">
        <div className="flex h-full flex-col md:flex-row">
          {/* Left side - Post content (only shown if there are images) */}
          {media && media.length > 0 ? (
            <div className="w-full md:w-3/5 flex-shrink-0 image-container">
              {modalReady ? (
                <NeoCarousel images={media} className="size-full neo-carousel" full={true} />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-black">
                  {/* Loading placeholder */}
                  <div className="h-full w-full animate-pulse bg-gray-800"></div>
                </div>
              )}
            </div>
          ) : null}

          {/* Right side - Comments */}
          <div className={`flex flex-col ${images && images.length > 0 ? 'w-full md:w-2/5' : 'w-full md:w-full'} h-full comments-container`}>
            {/* Post header - Only visible on desktop */}
            <div className="hidden md:flex justify-between items-center p-3 border-b">
              <div className="flex">
                <IonRouterLink routerLink={`/u/${user.username}`} onClick={onDidDismiss}>
                  <GolfAvatar imageUrl={user.imageUrl} username={user.username} name={`${user.firstName} ${user.lastName}`} size={40} />
                </IonRouterLink>
                <div className="ml-3">
                  <IonRouterLink routerLink={`/u/${user.username}`} onClick={onDidDismiss} className="no-underline">
                    <p className="font-semibold">{user.username}</p>
                  </IonRouterLink>
                  <p className="text-xs text-muted-foreground">{timeAgo(new Date(createdAt), t)}</p>
                </div>
              </div>
              {user.id === profileId && (
                <PostMenu
                  postId={String(postId)}
                  postUserId={user.id}
                  username={user.username}
                  onDelete={() => onDelete(postId)}
                />
              )}
            </div>

            {/* Caption */}
            {caption && (
              <div className="p-3 flex items-center">
                <p>{renderCaptionWithHashtags(caption)}</p>
              </div>
            )}

            {/* Location */}
            {course && (
              <div className="flex items-center bg-secondary rounded-xl m-3 px-3 py-2">
                <ImageWithFallback
                  alt={`${course.name} photo`}
                  fallbackImage="/assets/images/fallback.jpg"
                  src={`${IMAGES_BASE_URL}/${course.id}.jpg`}
                  className="size-8 shrink-0 overflow-hidden object-cover rounded mr-2"
                />
                <span className="flex-1 text-[15px] font-medium text-foreground">
                  {course.name}
                </span>
                {/* <IonIcon
                  icon={chevronForward}
                  size="small"
                  className="text-[#C2C2C2]"
                /> */}
              </div>
            )}

            <span className="text-sm mx-3 text-muted-foreground md:hidden">
              {timeAgo(new Date(createdAt), t)}
            </span>

            {/* Actions bar */}
            <div className="md:border-t md:border-b p-3 sticky bottom-0 bg-card z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <LikeButton
                    postId={postId}
                    initialLiked={isLiked}
                    initialLikeCount={likeCount}
                    userId={profileId?.toString() || ''}
                    showCount={false}
                    size="md"
                    className="flex items-center"
                    onLikeChange={(newIsLiked, newCount) => {
                      setIsLiked(newIsLiked);
                      setLikeCount(newCount);
                    }}
                  />
                  <button className="flex items-center" onClick={() => textareaRef.current?.focus()}>
                    <GolfIcon name="message" className="size-6" />
                  </button>
                  <button className="flex items-center" onClick={handleSharePost}>
                    <GolfIcon name="sent" className="size-6" />
                  </button>
                </div>
              </div>
            </div>

            {/* Comments section */}
            <div className="flex-1 overflow-y-auto p-3 comments-section">
              {isLoadingComments ? (
                <div className="flex justify-center p-4">
                  <p>{t('loading')}...</p>
                </div>
              ) : commentsList.length > 0 ? (
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold">{t('replies')}</h2>
                  {commentsList.map(comment => (
                    <div key={comment.id} className="flex items-center">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={comment.user.imageUrl || ''} alt={comment.user.username} />
                        <AvatarFallback>{comment.user.username[0]}</AvatarFallback>
                      </Avatar>
                      <div className="ml-2 flex-1">
                        <div className="flex justify-between">
                          <div>
                            <span className="font-semibold text-sm">{comment.user.username}</span>{' '}
                            <span className="text-sm">{comment.content}</span>
                            <div className="mt-1 flex items-center space-x-2">
                              <span className="text-xs text-muted-foreground">
                                {timeAgo(new Date(comment.createdAt), t)}
                              </span>
                              {comment.likes > 0 && (
                                <span className="text-xs text-muted-foreground">
                                  {comment.likes} {t('likes')}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => handleLikeComment(comment.id)}
                              className="p-1"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                width="24"
                                height="24"
                                className={cn(
                                  "size-5 transition-all duration-0",
                                  comment.isLiked ? "text-rose-500" : "text-foreground group-hover:text-rose-400"
                                )}
                                fill={comment.isLiked ? "currentColor" : "none"}
                              >
                                <path
                                  d="M19.4626 3.99415C16.7809 2.34923 14.4404 3.01211 13.0344 4.06801C12.4578 4.50096 12.1696 4.71743 12 4.71743C11.8304 4.71743 11.5422 4.50096 10.9656 4.06801C9.55962 3.01211 7.21909 2.34923 4.53744 3.99415C1.01807 6.15294 0.221721 13.2749 8.33953 19.2834C9.88572 20.4278 10.6588 21 12 21C13.3412 21 14.1143 20.4278 15.6605 19.2834C23.7783 13.2749 22.9819 6.15294 19.4626 3.99415Z"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </button>
                            {comment.user.username === user.username && (
                              <button
                                onClick={() => handleDeleteComment(comment.id)}
                                className="p-1 text-red-500"
                              >
                                <X className="size-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={commentsEndRef} />
                </div>
              ) : (
                <div className="flex h-full min-h-52 items-center justify-center">
                  <p className="text-muted-foreground">{t('no_comments_caption')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </IonContent>

      {/* Comment input */}
      <IonFooter
        ref={footerRef}
        className={`bg-card border-t md:border-none flex p-2 pb-safe-offset-1`}
      >
        <div className="flex-1 flex items-center">
          <TextChat
            ref={textareaRef}
            value={newComment}
            maxLength={1024}
            onChange={e => setNewComment(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleAddComment();
              }
            }}
            placeholder={t('send_comment_placeholder')}
            className="min-h-8 w-full resize-none border-0 bg-transparent p-2"
          />

          <button
            type="button"
            onClick={handleAddComment}
            className="p-2"
            disabled={!newComment.trim()}
          >
            <GolfIcon name="sent" className={`size-6 ${newComment.trim() ? 'text-primary' : 'text-muted-foreground'}`} />
          </button>
        </div>
      </IonFooter>
    </IonModal>
  );
};

export default OpenedPost;
